import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import BackChevronIconSvg from '../../../../assets/icons/icon-raffle-back-chevron.svg?react';
import professionalHeroBikeImage from '../../../../assets/images/raffle/raffle-professional-hero-bike.jpg';
import professionalNetworkImage from '../../../../assets/images/raffle/raffle-professional-network.jpg';
import professionalRule1Image from '../../../../assets/images/raffle/raffle-professional-rule-1.png';
import professionalRule2Image from '../../../../assets/images/raffle/raffle-professional-rule-2.png';
import professionalRule3Image from '../../../../assets/images/raffle/raffle-professional-rule-3.png';
import professionalRule4Image from '../../../../assets/images/raffle/raffle-professional-rule-4.png';
import professionalRule5Image from '../../../../assets/images/raffle/raffle-professional-rule-5.png';
import { zIndexMap } from '../../../../shared/config';
import { BaseButton, rubikTypographyStyles } from '../../../../shared/ui';
import { ReferralProgressCard } from '../../../../shared/ui/referral-progress-card';
import {
  RaffleCountdownCard,
  type RaffleCountdownLabels,
} from '../../shared/ui/raffle-countdown-card';
import {
  RaffleFaqSection,
  type RaffleFaqSectionProps,
} from '../../shared/ui/raffle-faq-section';
import { RafflePrimaryButton } from '../../shared/ui/raffle-primary-button';
import {
  RaffleRulesSection,
  type RaffleRuleStep,
  type RaffleRulesSectionProps,
} from '../../shared/ui/raffle-rules-section';
import {
  RaffleTicketBreakdownSection,
  type RaffleTicketBreakdownSectionProps,
} from '../../shared/ui/raffle-ticket-breakdown-section';

export type ProfessionalRaffleScreenProps = {
  backAriaLabel?: string;
  chancePercent?: number;
  className?: string;
  onPrimaryAction?: () => void;
  onBack?: () => void;
  primaryActionSupportText?: string | null;
  referralsCount?: number;
  ticketsCount?: number;
};

type ProfessionalRaffleHeroCopy = {
  imageAlt: string;
  note: string;
  prizeBrand: string;
  prizeModel: string;
  subtitle: string;
  title: string;
};

type ProfessionalRaffleCountdownCopy = {
  labels: RaffleCountdownLabels;
  title: string;
};

type ProfessionalRaffleRuleCopy = {
  descriptionParts: RaffleRuleStep['descriptionParts'];
};

type ProfessionalRaffleRulesCopy = Omit<
  RaffleRulesSectionProps,
  'className' | 'steps'
> & {
  steps: ReadonlyArray<ProfessionalRaffleRuleCopy>;
};

type ProfessionalRaffleTicketBreakdownCopy = Omit<
  RaffleTicketBreakdownSectionProps,
  'className' | 'diagramImage'
> & {
  diagramImageAlt: string;
};

const PROFESSIONAL_RULE_IMAGES = [
  professionalRule1Image,
  professionalRule2Image,
  professionalRule3Image,
  professionalRule4Image,
  professionalRule5Image,
] as const;

const DEFAULT_PROFESSIONAL_RAFFLE_TARGET_AT = '2026-08-01T00:00:00-03:00';
const PROFESSIONAL_RULE_IMAGE_WIDTH_PX = 150;
const PROFESSIONAL_RULE_TEXT_WIDTH_PX = 230;

function resolveProfessionalRuleSteps(
  steps: ReadonlyArray<ProfessionalRaffleRuleCopy>,
): ReadonlyArray<RaffleRuleStep> {
  return steps.map((step, index) => {
    const align = index % 2 === 0 ? 'right' : 'left';

    return {
      descriptionAlign: align,
      descriptionMaxWidthPx: PROFESSIONAL_RULE_TEXT_WIDTH_PX,
      descriptionParts: step.descriptionParts,
      imageAlign: align,
      imageMaxWidthPx: PROFESSIONAL_RULE_IMAGE_WIDTH_PX,
      imageSrc: PROFESSIONAL_RULE_IMAGES[index],
      number: String(index + 1).padStart(2, '0'),
    };
  });
}

const Page = styled.div.attrs({
  'data-ui': 'professional-raffle-screen',
})`
  ${rubikTypographyStyles};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const horizontalInsetStyles = css`
  box-sizing: border-box;
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(20)}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(20)}, env(safe-area-inset-left))`};
`;

const Frame = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(20)};
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(20)}, env(safe-area-inset-top))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(28)}, env(safe-area-inset-bottom))`};
`;

const TopBar = styled.div`
  ${horizontalInsetStyles};
  display: grid;
  justify-content: start;
`;

const BackButton = styled(BaseButton).attrs({
  type: 'button',
})`
  margin-left: ${({ theme }) => theme.scale.px(16)};
  width: ${({ theme }) => theme.scale.px(28)};
  height: ${({ theme }) => theme.scale.px(28)};
  display: inline-grid;
  place-items: center;
`;

const BackIcon = styled(BackChevronIconSvg).attrs({
  'aria-hidden': true,
})`
  width: ${({ theme }) => theme.scale.px(12)};
  height: ${({ theme }) => theme.scale.px(20)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeroSection = styled.section.attrs({
  'data-ui': 'professional-raffle-hero',
})`
  position: relative;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(14)};
  padding-bottom: 0;
`;

const HeroTitle = styled.h1`
  position: relative;
  z-index: ${zIndexMap.raffle.heroCopy};
  margin: 0;
  max-width: ${({ theme }) => theme.scale.px(340)};
  text-align: center;
  display: grid;
  gap: ${({ theme }) => theme.scale.px(2)};
`;

const HeroTitleLine = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.scale.px(36)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(36)};
`;

const HeroTitleAccent = styled(HeroTitleLine)`
  color: ${({ theme }) => theme.colors.brand.primary};
`;

const HeroSubtitle = styled.p`
  position: relative;
  z-index: ${zIndexMap.raffle.heroCopy};
  margin: ${({ theme }) => `${theme.scale.px(8)} 0 0`};
  text-align: center;
  max-width: ${({ theme }) => theme.scale.px(280)};
  font-size: ${({ theme }) => theme.scale.px(17)};
  line-height: ${({ theme }) => theme.scale.px(20)};
`;

const PrizeLockup = styled.div`
  position: relative;
  z-index: ${zIndexMap.raffle.heroCopy};
  width: ${({ theme }) => theme.scale.px(352)};
  min-height: ${({ theme }) => theme.scale.px(120)};
  margin-top: ${({ theme }) => theme.scale.px(10)};
`;

const PrizeBrand = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(96)};
  font-weight: 700;
  line-height: ${({ theme }) => theme.scale.px(90)};
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const PrizeModel = styled.p`
  position: absolute;
  right: ${({ theme }) => theme.scale.px(8)};
  bottom: 0;
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(40)};
  font-weight: 700;
  line-height: ${({ theme }) => theme.scale.px(40)};
  text-transform: uppercase;
`;

const HeroVisual = styled.div`
  position: relative;
  z-index: ${zIndexMap.raffle.heroVisual};
  justify-self: stretch;
  margin-top: ${({ theme }) => theme.scale.px(-12)};
  margin-bottom: ${({ theme }) => theme.scale.px(-44)};
`;

const HeroBike = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const HeroNote = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.scale.px(30)};
  left: ${({ theme }) => theme.scale.px(64)};
  z-index: ${zIndexMap.raffle.heroCopy};
  width: ${({ theme }) => theme.scale.px(132)};
  font-size: ${({ theme }) => theme.scale.px(11)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(11)};
  white-space: pre-line;
`;

const HeroNoteSpark = styled.svg.attrs({
  'aria-hidden': true,
  viewBox: '0 0 54 54',
})`
  position: absolute;
  top: ${({ theme }) => theme.scale.px(-38)};
  left: ${({ theme }) => theme.scale.px(-34)};
  width: ${({ theme }) => theme.scale.px(54)};
  height: ${({ theme }) => theme.scale.px(54)};
  color: ${({ theme }) => theme.colors.support.infoSubtle};
`;

const ContentStack = styled.div`
  ${horizontalInsetStyles};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(18)};
`;

const ExpandedRulesSection = styled(RaffleRulesSection)`
  justify-self: stretch;
  margin-left: ${({ theme }) => theme.scale.px(-10)};
  margin-right: ${({ theme }) => theme.scale.px(-10)};
`;

const ActionBlock = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const SupportText = styled.p`
  margin: 0;
  text-align: center;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(16)};
  color: ${({ theme }) => theme.colors.text.subdued};
`;

export function ProfessionalRaffleScreen({
  backAriaLabel,
  chancePercent = 0,
  className,
  onPrimaryAction,
  onBack,
  primaryActionSupportText,
  referralsCount = 0,
  ticketsCount = 0,
}: ProfessionalRaffleScreenProps) {
  const { t } = useTranslation();
  const actionSupportText = primaryActionSupportText ?? null;
  const hero = t('referralBonus.professionalRaffle.hero', {
    returnObjects: true,
  }) as ProfessionalRaffleHeroCopy;
  const countdown = t('referralBonus.professionalRaffle.countdown', {
    returnObjects: true,
  }) as ProfessionalRaffleCountdownCopy;
  const faq = t('referralBonus.professionalRaffle.faq', {
    returnObjects: true,
  }) as Pick<RaffleFaqSectionProps, 'heading' | 'items'>;
  const rules = t('referralBonus.professionalRaffle.rules', {
    returnObjects: true,
  }) as ProfessionalRaffleRulesCopy;
  const ticketBreakdown = t(
    'referralBonus.professionalRaffle.ticketBreakdown',
    {
      returnObjects: true,
    },
  ) as ProfessionalRaffleTicketBreakdownCopy;
  const ctaLabel = t('referralBonus.professionalRaffle.ctaLabel');
  const resolvedBackAriaLabel = backAriaLabel ?? t('referralBonus.backAria');
  const [heroTitleLine, heroTitleAccent = ''] = hero.title.split('\n');
  const ruleSteps = resolveProfessionalRuleSteps(rules.steps);

  return (
    <Page className={className}>
      <Frame>
        <TopBar>
          {onBack ? (
            <BackButton aria-label={resolvedBackAriaLabel} onClick={onBack}>
              <BackIcon />
            </BackButton>
          ) : null}
        </TopBar>
        <HeroSection>
          <HeroTitle>
            <HeroTitleLine>{heroTitleLine}</HeroTitleLine>
            {heroTitleAccent ? (
              <HeroTitleAccent>{heroTitleAccent}</HeroTitleAccent>
            ) : null}
          </HeroTitle>
          <HeroSubtitle>{hero.subtitle}</HeroSubtitle>
          <PrizeLockup>
            <PrizeBrand>{hero.prizeBrand}</PrizeBrand>
            <PrizeModel>{hero.prizeModel}</PrizeModel>
          </PrizeLockup>
          <HeroVisual>
            <HeroNote>
              <HeroNoteSpark fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M37.8219 27.3371L41.4194 30.1032C42.1871 30.6981 41.8342 31.9227 40.8676 32.0178L36.8098 32.4018C32.6696 32.7913 28.8814 34.8871 26.3496 38.1729L23.8645 41.3989C23.2694 42.1653 22.0433 41.812 21.9474 40.8464L21.5603 36.7926C21.1675 32.6566 19.067 28.8711 15.7759 26.3399L12.5447 23.8552C11.7771 23.2603 12.13 22.0357 13.0965 21.9406L17.1544 21.5566C21.2878 21.1651 25.0828 19.0713 27.6145 15.7855L30.0996 12.5595C30.6947 11.793 31.9208 12.1464 32.0168 13.112L32.4475 17.6253C32.8167 21.4614 34.7589 24.9815 37.8219 27.3371Z"
                  fill="currentColor"
                />
              </HeroNoteSpark>
              {hero.note}
            </HeroNote>
            <HeroBike src={professionalHeroBikeImage} alt={hero.imageAlt} />
          </HeroVisual>
        </HeroSection>

        <ContentStack>
          <RaffleCountdownCard
            labels={countdown.labels}
            targetAt={DEFAULT_PROFESSIONAL_RAFFLE_TARGET_AT}
            title={countdown.title}
          />
          <ReferralProgressCard
            chancePercent={chancePercent}
            referralsCount={referralsCount}
            ticketsCount={ticketsCount}
            variant="professional"
          />
          <ActionBlock>
            <RafflePrimaryButton type="button" onClick={onPrimaryAction}>
              {ctaLabel}
            </RafflePrimaryButton>
            {actionSupportText ? (
              <SupportText>{actionSupportText}</SupportText>
            ) : null}
          </ActionBlock>
          <ExpandedRulesSection
            note={rules.note}
            steps={ruleSteps}
            titleAccent={rules.titleAccent}
            title={rules.title}
          />
          <RaffleTicketBreakdownSection
            diagramImage={{
              alt: ticketBreakdown.diagramImageAlt,
              src: professionalNetworkImage,
            }}
            headline={ticketBreakdown.headline}
            headlineAccent={ticketBreakdown.headlineAccent}
            hideTotalCard={ticketBreakdown.hideTotalCard}
            subtitle={ticketBreakdown.subtitle}
            totalTickets={ticketBreakdown.totalTickets}
          />
          <ActionBlock>
            <RafflePrimaryButton type="button" onClick={onPrimaryAction}>
              {ctaLabel}
            </RafflePrimaryButton>
            {actionSupportText ? (
              <SupportText>{actionSupportText}</SupportText>
            ) : null}
          </ActionBlock>
          <RaffleFaqSection heading={faq.heading} items={faq.items} />
        </ContentStack>
      </Frame>
    </Page>
  );
}
